'use client';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { ButtonFragmentFragment } from '@fm-nx/contentful-types';
import { getLinkToParent } from '@fm-nx/navigation';
import { usePathname } from 'next/navigation';
import { StickyButton } from '../sticky-button/sticky-button';
import { Button } from '../button/button';

const BackLinkPage = dynamic(() => import('../back-link-page/back-link-page'), {
    ssr: false,
});

type PageIntroTextDisplayProps = {
    entryId?: string;
    overline?: string | null;
    headerText?: string | null;
    headerParagraph?: string | null;
    button?: ButtonFragmentFragment | null;
    tableOfContents?: { label: string; anchor: string }[];
};

// page intro text with optional parent page link
export const PageIntroTextDisplay = (props: PageIntroTextDisplayProps) => {
    const { overline, headerText, headerParagraph, button, entryId, tableOfContents } = props;
    const pathname = usePathname();
    const inspectorProps = useContentfulInspectorMode({ entryId });
    const parentLink = useMemo(() => {
        return getLinkToParent({ pathname });
    }, [pathname]);
    const hasParent = !!parentLink;
    const tocItems = tableOfContents ?? [];

    return (
        <div className="relative w-full mx-auto max-w-container">
            <div
                className={classNames('mt-14 overflow-hidden', {
                    'mb-10 md:mb-36': hasParent && button && tocItems.length === 0,
                    'mb-14 md:mb-24': hasParent && !button,
                    'mb-8 md:mb-14': !hasParent,
                })}
            >
                <div
                    className={classNames('mx-6 md:mx-0', {
                        'md:grid md:grid-cols-14 md:gap-6': hasParent,
                        'grid grid-cols-1 gap-4 md:grid-cols-14': !hasParent,
                    })}
                >
                    {hasParent && (
                        <div className="my-8 md:my-0 md:col-span-3 md:col-start-2">
                            <BackLinkPage />
                        </div>
                    )}
                    <div
                        className={classNames('space-y-6', {
                            'col-span-9': hasParent,
                            'md:col-span-8 md:col-start-2 lg:col-start-2': !hasParent,
                        })}
                    >
                        <h1 className="h5 overline">{overline}</h1>
                        <h2
                            {...inspectorProps({
                                fieldId: 'title',
                            })}
                            className={classNames({
                                'h-xl md:h-4xl': hasParent,
                                'h-xxl': !hasParent,
                            })}
                        >
                            {headerText}
                        </h2>
                        <p
                            {...inspectorProps({
                                fieldId: 'summary',
                            })}
                            className={classNames({
                                'h-md md:h-lg': hasParent,
                                'p-xl': !hasParent,
                            })}
                        >
                            {headerParagraph}
                        </p>

                        <div className="md:hidden">
                            {/* Hidden at md because StickyButton replaces it */}
                            {button && <Button button={button} />}
                        </div>

                        {tocItems.length === 0 && button && (
                            <div className="absolute z-50 right-8 md:right-16 -bottom-44 md:-bottom-56">
                                <StickyButton button={button} />
                            </div>
                        )}

                        {tocItems.length > 0 && (
                            <div className="flex gap-6 justify-between items-center relative">
                                <nav
                                    aria-label="Table of contents"
                                    className="bg-ivory-100 w-full py-6 relative md:bg-transparent md:pb-0
                                        before:content-[''] before:absolute before:w-screen before:h-full before:bg-ivory-100
                                        before:-z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 md:before:hidden"
                                >
                                    <h2 className="h5 overline mb-4 md:hidden">Table of Contents</h2>
                                    <ul
                                        className={`flex flex-col gap-6 ${button ? 'lg:flex-row lg:flex-wrap' : 'md:flex-row md:flex-wrap'}`}
                                    >
                                        {tocItems.map((item, index) => (
                                            <li key={item.anchor} className="flex items-center gap-2">
                                                <span className="h-xxs text-gray-500">
                                                    {String(index + 1).padStart(2, '0')}
                                                </span>
                                                <a
                                                    href={item.anchor}
                                                    className="h-xxs border-b-2 border-current pb-0.5"
                                                >
                                                    {item.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                                <div className="hidden md:block md:relative md:min-w-40">
                                    {button && <StickyButton button={button} />}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PageIntroTextDisplay;
